import { Injectable, OnDestroy, TemplateRef } from '@angular/core';
import {
    CmpsDialog,
    CmpsDialogRef,
    CmpsSnackBar,
    CmpsTextOnlySnackBarComponent,
    CmpsTextOnlySnackBarOptions,
} from '@cmps/ng-uikit';
import { tap } from 'rxjs/operators';

import { takeUntilDestroy } from '../../common/take-until-destroy.decorator';
import { LoggerService } from '../../logger';
import { HudComponent } from '../hud';

import { MicrophoneRecorder } from './microphone-recorder';
import { CallHandle } from './model/call-handle.model';
import { CallState } from './model/call-state.model';
import { HubMethods } from './model/hub-methods.model';
import { StartCallParams } from './model/start-call-params.model';
import { SocketService } from './socket.service';

@Injectable({ providedIn: 'root' })
export class CallService implements OnDestroy {
    callHandle: CallHandle | null;

    /* TODO refactor */
    buttons: Array<TemplateRef<any>>;

    private _recorder: MicrophoneRecorder;
    private _hudRef: CmpsDialogRef<HudComponent, void>;

    constructor(
        private readonly _socketService: SocketService,
        private readonly _dialog: CmpsDialog,
        private readonly _logger: LoggerService,
        private readonly _snackBar: CmpsSnackBar,
    ) {
        this._bind();
    }

    ngOnDestroy(): void {
        this._hideHud();
        void this._recorder?.stop();
    }

    start(params: StartCallParams): void {
        params.cpeId = this._logger.cpeId;
        this._logger.debug({ params }, 'start');
        void this._socketService.startCall(params);
    }

    end(): void {
        this._logger.debug('end');
        if (this.callHandle?.callHandleId != null) {
            this._logger.debug(
                {
                    callHandleId: this.callHandle.callHandleId,
                },
                'end',
            );
            void this._socketService.endCall({
                callHandleId: this.callHandle.callHandleId,
            });
        }
    }

    private _bind(): void {
        this._socketService.message
            .pipe(
                tap((msg) => {
                    switch (msg.hubMethod) {
                        case HubMethods.OnRetrieveCurrentCall:
                            this._onCurrentCall(msg.payload);
                            break;
                        case HubMethods.OnCallState:
                            this._onCallState(msg.payload);
                            break;
                    }
                }),
                takeUntilDestroy(this),
            )
            .subscribe();

        this._socketService.didConnect
            .pipe(
                tap(() => {
                    const data: CmpsTextOnlySnackBarOptions = {
                        text: 'connected to local call socket',
                    };
                    this._snackBar.open(CmpsTextOnlySnackBarComponent, {
                        duration: 15000,
                        placement: 'bottom-right',
                        data,
                    });
                }),
                takeUntilDestroy(this),
            )
            .subscribe();

        this._socketService.didDisconnect
            .pipe(
                tap((stopped) => {
                    if (stopped) {
                        return;
                    }
                    const data: CmpsTextOnlySnackBarOptions = {
                        text: 'disconnected from local call socket',
                    };
                    this._snackBar.open(CmpsTextOnlySnackBarComponent, {
                        duration: 15000,
                        placement: 'bottom-right',
                        data,
                    });
                }),
                takeUntilDestroy(this),
            )
            .subscribe();
    }

    private _onCurrentCall(payload: CallHandle | null): void {
        this.callHandle = payload;

        const startedState = payload?.stateHistory?.find(
            (e) => e.state === CallState.Started,
        );
        const endedState = payload?.stateHistory?.find(
            (e) => e.state === CallState.Ended,
        );

        if (startedState != null && endedState == null) {
            this._logger.debug({
                cpeId: this._logger.cpeId,
                callHandleCpeId: this.callHandle?.cpeId,
            });
            if (this.callHandle?.cpeId === this._logger.cpeId) {
                this._setupRecorder();
                void this._recorder.start();
            }
            this._showHud();
        } else {
            this._hideHud();
            void this._recorder?.stop();
        }
    }

    private _onCallState(payload: CallState | null): void {
        if (payload === CallState.Ended) {
            // preemptively null current call. even though HubMethods.OnRetrieveCurrentCall should be incoming soon
            this._onCurrentCall(null);
        }
    }

    private _setupRecorder(): void {
        this._recorder = new MicrophoneRecorder();
        this._recorder.dataAvailable = (data) => {
            // always check call handle in case call is ended but MediaRecorder hasn't stopped or been cleaned up yet
            if (this.callHandle == null) {
                return;
            }

            let err = null;
            try {
                const reader = new FileReader();
                reader.onload = (e) => {
                    const dataUrl = e.target.result as string;
                    const base64 = dataUrl.split(',')[1];
                    void this._socketService.writeCallAudioData({
                        callHandleId: this.callHandle.callHandleId,
                        data: base64,
                    });
                };
                reader.readAsDataURL(data);
            } catch (e) {
                err = e;
            }
            if (err != null || !this._socketService.connected) {
                let msg = 'failed to send audio chunk';
                if (err == null && !this._socketService.connected) {
                    msg = 'trying to send audio chunk';
                }
                const data: CmpsTextOnlySnackBarOptions = {
                    text: msg,
                };
                this._snackBar.open(CmpsTextOnlySnackBarComponent, {
                    duration: 1000,
                    placement: 'top-right',
                    data,
                });
            }
        };
        this._recorder.error = (error) => {
            alert(error.message);
        };
    }

    private _showHud(): void {
        if (this._hudRef != null && !this._hudRef.closed) {
            return;
        }

        this._hudRef = this._dialog.open(HudComponent, {
            disableOverlayOverflowHidden: true,
            overlayClass: 'cmps-localcall-hud--overlay-class',
            // portalClass: 'cmps-localcall-hud--portal-class',
            portalWrapperClass: 'cmps-localcall-hud--portal-wrapper-class',
            disableBackdrop: true,
            disableClose: true,
            // movable: true,
            placement: 'top-center',
        });
    }

    private _hideHud(): void {
        this._hudRef?.close();
        this._hudRef = null;
    }
}
